import { z } from "zod";
import { zGuard } from "../zod/utils.js";

import { DisplayTableColumnOutputType } from "../display-table/outputTypes.js";
import { HqlTruncUnit, HqlTruncUnitLiteral } from "../hql/types.js";
import {
  ExploreCustomTimeUnit,
  ExploreField,
  ExploreTimeUnit,
  ExploreTypeSelectorOption,
  ExploreViewType,
  ExploreVisualizationType,
  HexSLTypes,
} from "../index.js";
import {
  ExploreCellDataframe,
  getDataframeName,
} from "../sql/dataSourceTableConfig.js";

export function getQueryModeDisabledDataframeName(
  dataframe: ExploreCellDataframe,
): string {
  return `${getDataframeName(dataframe)}_df`;
}

export const HexSLTypeMapping: Record<
  NonNullable<HexSLTypes.DimensionType>,
  DisplayTableColumnOutputType
> = {
  ["boolean"]: DisplayTableColumnOutputType.BOOLEAN,
  ["date"]: DisplayTableColumnOutputType.DATE,
  ["null"]: DisplayTableColumnOutputType.UNKNOWN,
  ["number"]: DisplayTableColumnOutputType.NUMBER,
  ["other"]: DisplayTableColumnOutputType.UNKNOWN,
  ["string"]: DisplayTableColumnOutputType.STRING,
  ["time"]: DisplayTableColumnOutputType.TIME,
  ["timestamp"]: DisplayTableColumnOutputType.DATETIME,
  ["timestamptz"]: DisplayTableColumnOutputType.DATETIMETZ,
};

// in hex-sl, these measure agg types all produce numeric results
export const NumericMeasureAggTypes = z.enum([
  "count",
  "countdistinct",
  "sum",
  "avg",
  "min",
  "max",
  "median",
  "stddev",
  "stddevpop",
  "variance",
  "variancepop",
  "sumboolean",
]);

export type NumericMeasureAggTypes = z.infer<typeof NumericMeasureAggTypes>;

export function exploreFieldIsDateTimeType(field: ExploreField): boolean {
  return (
    field.dataType === "DATE" ||
    field.dataType === "TIMESTAMP" ||
    field.dataType === "TIMESTAMPTZ"
  );
}

export const ExploreTruncationOptions = HqlTruncUnitLiteral.options.filter(
  (value) => value !== "dayofweek",
);

export function getCoarserTruncUnit(
  truncUnit: ExploreTimeUnit | undefined,
): HqlTruncUnit | undefined {
  if (zGuard(ExploreCustomTimeUnit, truncUnit)) {
    return undefined;
  }

  switch (truncUnit) {
    case "second":
      return "minute";
    case "minute":
      return "hour";
    case "hour":
      return "day";
    case "day":
      return "week";
    case "week":
    case undefined: // default to month if no trunc unit set
      return "month";
    case "month":
      return "quarter";
    case "quarter":
      return "year";
    case "year":
    case "dayofweek":
      return undefined;
    default:
      return undefined;
  }
}

export function getCoarserBinCount(
  binCount: number | undefined,
): number | undefined {
  if (binCount == null) {
    return 6;
  }
  const coarserBinCount = Math.floor(binCount / 2);
  return coarserBinCount > 1 ? coarserBinCount : undefined;
}

export const getDefaultViewTypeForVizType = (
  vizType: ExploreVisualizationType | ExploreTypeSelectorOption,
): ExploreViewType => {
  return vizType === "pivot-table" ? "visualization" : "both";
};
